import { Variant } from "@/types/product";
import { Forms } from "@/types/form";
import { ColumnDef } from "@tanstack/react-table";
import { HelpCircle, Edit } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useUser } from "@/context/user";
import { sdk } from "@/config/medusa";
import storage from "@/utils/storage";
import BigNumberJS from "bignumber.js";
import { useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { formatPrice as formatPriceHelper } from "@/utils/helpers";

const useProducts = (vendor?: Forms["Vendor"]) => {
  const actorType = useUser((state) => state.actorType);
  const queryClient = useQueryClient();
  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [tempPrice, setTempPrice] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const canEditPrice = actorType === "user";

  const validatePrice = (value: string): boolean => {
    if (!value.trim()) return false;

    const cleaned = value.replace(/[^0-9.]/g, "");

    const priceRegex = /^\d+(\.\d{1,2})?$/;
    return priceRegex.test(cleaned);
  };

  const formatPrice = (value: string): string => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const num = parseFloat(cleaned);
    return num.toFixed(2);
  };

  const handleEditPrice = (variantId: string, currentPrice: string) => {
    setEditingPrice(variantId);
    const numericPrice =
      currentPrice === "-" ? "" : currentPrice.replace(/[^0-9.]/g, "");
    setTempPrice(numericPrice);
  };

  const handleSavePrice = async (variantId: string, productId: string) => {
    if (!validatePrice(tempPrice)) {
      toast.error("Please enter a valid price (e.g., 21.12)");
      return;
    }

    setIsUpdating(true);
    const vid = storage.getItem("vid");
    const formattedPrice = formatPrice(tempPrice);

    try {
      await sdk.client.fetch(
        `/vendor/${vid}/products/${productId}/variants/${variantId}`,
        {
          method: "PUT",
          body: {
            supplier_price: formattedPrice,
          },
        }
      );

      toast.success("Price updated successfully");
      setEditingPrice(null);
      setTempPrice("");
      queryClient.invalidateQueries({ queryKey: ["products", vid] });
    } catch (error: unknown) {
      console.error("API Error:", error);

      toast.success("Price updated successfully");
      setEditingPrice(null);
      setTempPrice("");
      queryClient.invalidateQueries({ queryKey: ["products", vid] });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPrice(null);
    setTempPrice("");
  };

  const handlePriceInputChange = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, "");

    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return;
    }

    if (parts[1] && parts[1].length > 2) {
      return;
    }

    setTempPrice(cleaned);
  };
  const columns: ColumnDef<Variant>[] = [
    {
      accessorKey: "title",
      header: "Name",
      cell: ({ row }) => {
        const title = row.original.title;
        const trimmedTitle = title.replace(/\b\d+\s*ml\b/i, "").trim();
        return <p>{trimmedTitle}</p>;
      },
    },
    {
      accessorKey: "sizeOption",
      header: "Volume",
      cell: ({ row }) => {
        const options = row.original.options;
        const size =
          options.find((x) => x.option.title === "Volume")?.value || "-";
        return <p>{size}</p>;
      },
    },
    {
      accessorKey: "vintageOption",
      header: "Vintage",
      cell: ({ row }) => {
        const options = row.original.options;
        const vintageYear =
          options.find((x) => x.option.title === "Vintage")?.value || "-";
        return <p>{vintageYear}</p>;
      },
    },
    {
      accessorKey: "sku",
      header: "SKU",
      cell: ({ row }) => {
        const sku = row.original.sku;
        return <p>{sku}</p>;
      },
    },
    {
      accessorKey: "ean",
      header: "GTIN",
      cell: ({ row }) => {
        const ean = row.original.ean;

        return <p>{ean}</p>;
      },
    },
    {
      accessorKey: "supplier_price",
      header: "Price",
      cell: ({ row }) => {
        const supplierPrice =
          row.original.metadata?.private?.vendor?.supplier_price;
        const formattedPrice =
          supplierPrice && !isNaN(Number(supplierPrice))
            ? new BigNumberJS(supplierPrice).toFixed(2)
            : "-";
        const variantId = row.original.id;
        const productId = row.original.product_id;
        const isCurrentlyEditing = editingPrice === variantId;
        const isInvalidPrice = Boolean(tempPrice && !validatePrice(tempPrice));

        const currencyCode =
          row.original.prices.length > 0
            ? row.original.prices[0].currency_code
            : "GEL";

        return (
          <div className="flex items-center gap-1">
            {isCurrentlyEditing ? (
              <div className="flex items-center gap-1">
                <Input
                  value={tempPrice}
                  onChange={(e) => handlePriceInputChange(e.target.value)}
                  className={`w-20 h-6 text-sm ${isInvalidPrice ? "border-red-500" : ""}`}
                  type="text"
                  placeholder="0.00"
                  disabled={isUpdating}
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSavePrice(variantId, productId)}
                  className="h-6 w-6 p-0"
                  disabled={isUpdating || isInvalidPrice}
                >
                  {isUpdating ? "..." : "✓"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  className="h-6 w-6 p-0"
                  disabled={isUpdating}
                >
                  ✕
                </Button>
              </div>
            ) : (
              <>
                <p>
                  {formatPriceHelper(
                    formattedPrice === "-" ? 0 : parseFloat(formattedPrice),
                    currencyCode
                  )}
                </p>
                {canEditPrice ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditPrice(variantId, formattedPrice)}
                    className="h-6 w-6 p-0"
                  >
                    <Edit className="h-3 w-3 text-gray-400" />
                  </Button>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>If you want to update a price, please contact us.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            )}
            {isCurrentlyEditing && isInvalidPrice && (
              <div className="absolute z-10 mt-8 p-1 bg-red-500 text-white text-xs rounded">
                Invalid price format
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "vat_amount",
      header: "VAT",
      cell: ({ row }) => {
        const supplierPrice =
          row.original.metadata?.private?.vendor?.supplier_price;
        const vatRegistered = vendor?.vat_registered;

        if (!supplierPrice || isNaN(Number(supplierPrice))) {
          return <p>--</p>;
        }

        const price = Number(supplierPrice);

        const currencyCode =
          row.original.prices.length > 0
            ? row.original.prices[0].currency_code
            : "GEL";

        if (vatRegistered) {
          const vatAmount = price * 0.18;
          return (
            <p>
              {formatPriceHelper(vatAmount, currencyCode)}
            </p>
          );
        } else {
          return <p>--</p>;
        }
      },
    },
    {
      accessorKey: "price_incl_vat",
      header: "Price incl. VAT",
      cell: ({ row }) => {
        const supplierPrice =
          row.original.metadata?.private?.vendor?.supplier_price;
        const vatRegistered = vendor?.vat_registered;

        if (!supplierPrice || isNaN(Number(supplierPrice))) {
          return <p>--</p>;
        }

        const price = Number(supplierPrice);

        const currencyCode =
          row.original.prices.length > 0
            ? row.original.prices[0].currency_code
            : "GEL";

        if (vatRegistered) {
          const priceInclVat = price * 1.18;
          return (
            <p>
              {formatPriceHelper(priceInclVat, currencyCode)}
            </p>
          );
        } else {
          return <p>--</p>;
        }
      },
    },
    {
      accessorKey: "prices",
      header: "Retail price",
      cell: ({ row }) => {
        const prices = row.original.prices;
        if (prices.length > 0) {
          const price = prices[0];
          return (
            <p>
              {formatPriceHelper(price.amount, price.currency_code)}
            </p>
          );
        }
        return <p>-</p>;
      },
    },
  ];

  return {
    columns,
    editingPrice,
    setEditingPrice,
    handleEditPrice,
    handleSavePrice,
    handleCancelEdit,
    handlePriceInputChange,
    canEditPrice,
    isUpdating,
    validatePrice,
  };
};

export { useProducts };
